import React, { useEffect, useState } from 'react';
import { Radio } from 'lucide-react';
import { useLiveDonates } from '../../hooks/live-donate/useLiveDonates';
import { useMediaQuery } from '../../hooks/use-media-query';
import LiveDonateList from './live-donate-list';

interface LiveDonateFeedProps {
  /**
   * Ngưỡng (px) để coi màn hình là "máy tính" — khớp với breakpoint Tailwind mà cột chứa khung
   * này thật sự hiện ra. Mặc định 1024 (`lg`, cột phải AsideRight của người đã đăng nhập). Khách
   * vãng lai dùng 1280 (`xl`) vì cột phải của họ (hashtag/quảng cáo trong home.page.tsx) chỉ hiện
   * từ `xl` trở lên.
   */
  desktopMinWidth?: number;
}

/**
 * Khung "Donate trực tiếp" ở cột phải trang chủ (máy tính).
 *
 * Cột chứa khung này bị ẩn bằng CSS chứ vẫn được gắn vào cây component ở màn hình điện thoại
 * (cột phải người đăng nhập dùng `hidden lg:block`, cột phải khách vãng lai dùng `md:hidden
 * xl:block`), và dải `LiveDonateTicker` cũng vậy ở màn hình máy tính. Theo dõi kích thước màn
 * hình thật (qua `useMediaQuery`, tự cập nhật khi đổi cỡ cửa sổ hoặc khi hồ sơ tải xong đổi bố
 * cục) — chứ không chỉ đọc một lần lúc gắn — để quyết định bên nào thật sự mở kết nối, tránh mở 2
 * kết nối SignalR cùng lúc hoặc bỏ sót việc mở/đóng lại khi ngưỡng đổi.
 */
const LiveDonateFeed: React.FC<LiveDonateFeedProps> = ({ desktopMinWidth = 1024 }) => {
  const isDesktop = useMediaQuery(desktopMinWidth);
  const { items, freshId } = useLiveDonates(8, isDesktop);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  if (!isDesktop) return null;

  return (
    <div className="ui-card mb-4 overflow-hidden p-3">
      <div className="mb-2 flex items-center gap-2">
        <Radio size={16} className="text-red-500" />
        <h3 className="text-base font-semibold text-gray-800 dark:text-white">Donate trực tiếp</h3>
      </div>
      {items.length ? (
        <LiveDonateList items={items} freshId={freshId} now={now} />
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">Chưa có lượt donate nào đủ lớn gần đây.</p>
      )}
    </div>
  );
};

export default LiveDonateFeed;
